import { Key, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ContactFormType } from '@/types';
import { TMDXFile } from '@/api/apis/files';
import { Contact } from '@/api/apis/facilities';
import { getFullName } from '@/utils/helpers';
import { mapContactsToSelectItems } from '../utils/mapContactsToSelectItems';

type PrimaryContactFormValues = ContactFormType & { primaryContactId: string | null };

type UsePrimaryContactHandlersParams = {
  list?: Contact[];
};

export const usePrimaryContactHandlers = ({ list }: UsePrimaryContactHandlersParams) => {
  const { control, setValue } = useFormContext<PrimaryContactFormValues>();

  const [photoPath, firstName, lastName] = useWatch({ control, name: ['photoPath', 'firstName', 'lastName'] });

  const selectItems = useMemo(() => mapContactsToSelectItems(list), [list]);

  const clearPrimaryContact = () => {
    setValue('primaryContactId', null, { shouldDirty: true });
    setValue('firstName', '', { shouldDirty: true });
    setValue('lastName', '', { shouldDirty: true });
    setValue('email', '', { shouldDirty: true });
    setValue('primaryPhone', '', { shouldDirty: true });
    setValue('secondaryPhone', '', { shouldDirty: true });
    setValue('photoPath', '', { shouldDirty: true });
    setValue('photo', undefined, { shouldDirty: true });
  };

  const onContactChange = (contactId: Key) => {
    if (!list) return;

    const newContact = list.find((c) => c.id === contactId);
    if (!newContact) {
      clearPrimaryContact();
      return;
    }

    setValue('primaryContactId', newContact.id, { shouldDirty: true });
    setValue('firstName', newContact.firstName || '', { shouldDirty: true });
    setValue('lastName', newContact.lastName || '', { shouldDirty: true });
    setValue('photoPath', newContact.photoPath || '', { shouldDirty: true });
    setValue('email', newContact.email || '', { shouldDirty: true });
    setValue('primaryPhone', newContact.primaryPhone || '', { shouldDirty: true });
    setValue('secondaryPhone', newContact.secondaryPhone || '', { shouldDirty: true });
    setValue('photo', undefined, { shouldDirty: true });
  };

  const onImageChange = (file: TMDXFile) => {
    setValue('photo', file, { shouldDirty: true });
  };

  const primaryContactName = getFullName(firstName, lastName);

  return {
    selectItems,
    clearPrimaryContact,
    onContactChange,
    onImageChange,
    photoPath,
    primaryContactName,
  };
};
